import React, { createContext, useContext, useEffect, useState } from 'react'
import { User } from '@supabase/supabase-js'
import { supabase, Profile } from '../lib/supabase'
import toast from 'react-hot-toast'

interface AuthContextType {
  user: User | null
  profile: Profile | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<void>
  signUp: (email: string, password: string, userData: { name: string; role: 'developer' | 'merchant'; company?: string }) => Promise<void>
  signOut: () => Promise<void>
  updateProfile: (updates: Partial<Profile>) => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [loading, setLoading] = useState(true)

  // Mock users for demo
  const mockUsers = {
    admin: {
      id: 'admin-user-id',
      email: 'admin@payfuse.dev',
      name: 'Admin PayFuse',
      role: 'admin',
      company: 'PayFuse Inc.'
    },
    developer: {
      id: 'developer-user-id',
      email: 'developer@payfuse.dev',
      name: 'Développeur PayFuse',
      role: 'developer',
      company: 'Tech Solutions'
    },
    merchant: {
      id: 'merchant-user-id',
      email: 'merchant@payfuse.dev',
      name: 'Commerçant PayFuse',
      role: 'merchant',
      company: 'Boutique en Ligne'
    }
  }

  // Fonction pour charger le profil utilisateur
  const loadProfile = async (userId: string): Promise<Profile | null> => {
    try {
      console.log('Chargement du profil pour userId:', userId)
      
      // Vérifier si c'est un utilisateur de démo
      if (userId === 'admin-user-id' || userId === 'developer-user-id' || userId === 'merchant-user-id') {
        let mockProfile: Profile
        
        if (userId === 'admin-user-id') {
          mockProfile = {
            id: 'admin-profile-id',
            user_id: userId,
            name: mockUsers.admin.name,
            email: mockUsers.admin.email,
            role: 'admin',
            company: mockUsers.admin.company,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }
        } else if (userId === 'developer-user-id') {
          mockProfile = {
            id: 'developer-profile-id',
            user_id: userId,
            name: mockUsers.developer.name,
            email: mockUsers.developer.email,
            role: 'developer',
            company: mockUsers.developer.company,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }
        } else {
          mockProfile = {
            id: 'merchant-profile-id',
            user_id: userId,
            name: mockUsers.merchant.name,
            email: mockUsers.merchant.email,
            role: 'merchant',
            company: mockUsers.merchant.company,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }
        }
        
        setProfile(mockProfile)
        return mockProfile
      }
      
      // Essayer de charger depuis Supabase
      try {
        const { data: userProfile, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', userId)
          .single()
        
        if (error) {
          console.warn('Erreur chargement profil:', error.message)
          throw error
        }
        
        if (userProfile) {
          console.log('Profil chargé:', userProfile)
          setProfile(userProfile)
          return userProfile
        }
      } catch (error) {
        console.error('Erreur Supabase:', error)
      }
      
      // Si aucun profil n'est trouvé, créer un profil par défaut
      console.warn('Aucun profil trouvé pour userId:', userId)
      
      try {
        const { data: newProfile, error } = await supabase
          .from('profiles')
          .insert([{
            user_id: userId,
            name: 'Utilisateur PayFuse',
            email: user?.email || 'user@example.com',
            role: 'merchant'
          }])
          .select()
          .single()
        
        if (error) {
          console.error('Erreur création profil par défaut:', error)
          throw error
        }
        
        console.log('Profil par défaut créé:', newProfile)
        setProfile(newProfile)
        return newProfile
      } catch (createError) {
        console.error('Erreur création profil par défaut:', createError)
        
        // Fallback à un profil en mémoire si Supabase échoue
        const fallbackProfile: Profile = {
          id: `profile-${Date.now()}`,
          user_id: userId,
          name: 'Utilisateur PayFuse',
          email: user?.email || 'user@example.com',
          role: 'merchant',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
        
        setProfile(fallbackProfile)
        return fallbackProfile
      }
    } catch (error) {
      console.error('Erreur chargement profil:', error)
      setProfile(null)
      return null
    }
  }

  // Initialisation de l'authentification
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setLoading(true)
        console.log('Initialisation de l\'authentification...')
        
        // Récupérer la session actuelle
        const { data: { session } } = await supabase.auth.getSession()
        console.log('Session récupérée:', session ? 'Oui' : 'Non')
        
        if (session?.user) {
          setUser(session.user)
          await loadProfile(session.user.id)
        } else {
          setUser(null)
          setProfile(null)
        }
      } catch (error) {
        console.error('Erreur initialisation auth:', error)
        setUser(null)
        setProfile(null)
      } finally {
        setLoading(false)
        console.log('Initialisation auth terminée')
      }
    }

    initializeAuth()

    // Écouter les changements d'état d'authentification
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Changement d\'état auth:', event, session ? 'Session active' : 'Pas de session')
        
        try {
          if (session?.user) {
            setUser(session.user)
            await loadProfile(session.user.id)
          } else {
            setUser(null)
            setProfile(null)
          }
        } catch (error) {
          console.error('Erreur lors du changement d\'état auth:', error)
          setUser(null)
          setProfile(null)
        } finally {
          setLoading(false)
        }
      }
    )

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true)
      console.log('Tentative de connexion pour:', email)
      
      // Gestion des comptes de démonstration
      if (email === 'admin@payfuse.dev' && password === 'PayFuseBolt') {
        // Simuler une connexion admin réussie
        const mockUser = {
          id: mockUsers.admin.id,
          email: email,
          app_metadata: {},
          user_metadata: {},
          aud: 'authenticated',
          created_at: new Date().toISOString()
        } as User
        
        const mockProfile: Profile = {
          id: 'admin-profile-id',
          user_id: mockUser.id,
          name: mockUsers.admin.name,
          email: email,
          role: 'admin',
          company: mockUsers.admin.company,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
        
        setUser(mockUser)
        setProfile(mockProfile)
        toast.success('Connecté en tant qu\'administrateur!')
        return
      } else if (email === 'developer@payfuse.dev' && password === 'PayFuseBolt') {
        // Simuler une connexion développeur réussie
        const mockUser = {
          id: mockUsers.developer.id,
          email: email,
          app_metadata: {},
          user_metadata: {},
          aud: 'authenticated',
          created_at: new Date().toISOString()
        } as User
        
        const mockProfile: Profile = {
          id: 'developer-profile-id',
          user_id: mockUser.id,
          name: mockUsers.developer.name,
          email: email,
          role: 'developer',
          company: mockUsers.developer.company,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
        
        setUser(mockUser)
        setProfile(mockProfile)
        toast.success('Connecté en tant que développeur!')
        return
      } else if (email === 'merchant@payfuse.dev' && password === 'PayFuseBolt') {
        // Simuler une connexion commerçant réussie
        const mockUser = {
          id: mockUsers.merchant.id,
          email: email,
          app_metadata: {},
          user_metadata: {},
          aud: 'authenticated',
          created_at: new Date().toISOString()
        } as User
        
        const mockProfile: Profile = {
          id: 'merchant-profile-id',
          user_id: mockUser.id,
          name: mockUsers.merchant.name,
          email: email,
          role: 'merchant',
          company: mockUsers.merchant.company,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
        
        setUser(mockUser)
        setProfile(mockProfile)
        toast.success('Connecté en tant que commerçant!')
        return
      }
      
      // Tentative de connexion réelle avec Supabase
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })
      
      if (error) throw error
      
      console.log('Connexion réussie, utilisateur:', data.user?.id)
      
      if (data.user) {
        setUser(data.user)
        await loadProfile(data.user.id)
      }
      
      toast.success('Connexion réussie!')
    } catch (error: any) {
      console.error('Erreur de connexion:', error)
      toast.error(error.message || 'Erreur lors de la connexion')
      throw error
    } finally {
      setLoading(false)
    }
  }

  const signUp = async (
    email: string, 
    password: string, 
    userData: { name: string; role: 'developer' | 'merchant'; company?: string }
  ) => {
    try {
      setLoading(true)
      console.log('Tentative de création de compte pour:', email)
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      })
      
      if (error) throw error
      
      console.log('Compte créé, utilisateur:', data.user?.id)
      
      if (data.user) {
        // Créer le profil utilisateur
        try {
          const { data: newProfile, error: profileError } = await supabase
            .from('profiles')
            .insert([{
              user_id: data.user.id,
              name: userData.name,
              email: email,
              role: userData.role,
              company: userData.company
            }])
            .select()
            .single()
          
          if (profileError) throw profileError
          
          console.log('Profil créé:', newProfile)
          setUser(data.user)
          setProfile(newProfile)
          
          toast.success('Compte créé avec succès!')
        } catch (profileError) {
          console.error('Erreur création profil:', profileError)
          
          // Fallback à un profil en mémoire
          const fallbackProfile: Profile = {
            id: `profile-${Date.now()}`,
            user_id: data.user.id,
            name: userData.name,
            email: email,
            role: userData.role,
            company: userData.company,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }
          
          setUser(data.user)
          setProfile(fallbackProfile)
          
          toast.success('Compte créé, mais erreur lors de la création du profil')
        }
      }
    } catch (error: any) {
      console.error('Erreur création compte:', error)
      toast.error(error.message || 'Erreur lors de la création du compte')
      throw error
    } finally {
      setLoading(false)
    }
  }

  const signOut = async () => {
    try {
      setLoading(true)
      console.log('Tentative de déconnexion')
      
      // Pour les utilisateurs de démo, simplement réinitialiser l'état
      if (user?.id === 'admin-user-id' || user?.id === 'developer-user-id' || user?.id === 'merchant-user-id') {
        setUser(null)
        setProfile(null)
        toast.success('Déconnexion réussie!')
        return
      }
      
      // Déconnexion réelle avec Supabase
      const { error } = await supabase.auth.signOut()
      if (error) throw error
      
      setUser(null)
      setProfile(null)
      
      toast.success('Déconnexion réussie!')
    } catch (error: any) {
      console.error('Erreur déconnexion:', error)
      toast.error(error.message || 'Erreur lors de la déconnexion')
      throw error
    } finally {
      setLoading(false)
    }
  }

  const updateProfile = async (updates: Partial<Profile>) => {
    if (!user) throw new Error('Utilisateur non connecté')
    
    try {
      setLoading(true)
      console.log('Mise à jour du profil:', updates)
      
      // Pour les utilisateurs de démo, mettre à jour localement
      if (user.id === 'admin-user-id' || user.id === 'developer-user-id' || user.id === 'merchant-user-id') {
        const updatedProfile = {
          ...profile!,
          ...updates,
          updated_at: new Date().toISOString()
        }
        
        setProfile(updatedProfile)
        toast.success('Profil mis à jour!')
        return updatedProfile
      }
      
      // Mise à jour réelle avec Supabase
      try {
        const { data: updatedProfile, error } = await supabase
          .from('profiles')
          .update({
            ...updates,
            updated_at: new Date().toISOString()
          })
          .eq('user_id', user.id)
          .select()
          .single()
        
        if (error) throw error
        
        console.log('Profil mis à jour:', updatedProfile)
        setProfile(updatedProfile)
        toast.success('Profil mis à jour!')
        
        return updatedProfile
      } catch (error) {
        console.error('Erreur Supabase:', error)
        
        // Fallback à une mise à jour locale
        const updatedProfile = {
          ...profile!,
          ...updates,
          updated_at: new Date().toISOString()
        }
        
        setProfile(updatedProfile)
        toast.success('Profil mis à jour localement')
        
        return updatedProfile
      }
    } catch (error: any) {
      console.error('Erreur mise à jour profil:', error)
      toast.error(error.message || 'Erreur lors de la mise à jour du profil')
      throw error
    } finally {
      setLoading(false)
    }
  }

  const value = {
    user,
    profile,
    loading,
    signIn,
    signUp,
    signOut,
    updateProfile,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}